import { Admin, Resource, useTranslate } from "react-admin";
import { useDataProvider } from "@pointment/ra-data-typegraphql";
import { Helmet } from "react-helmet";

import CompanyEdit from "components/CompanyEdit";
import CompanyList from "components/CompanyList";
import CompanyShow from "components/CompanyShow";
import { Layout } from "components/Layout";
import LocationCreate from "components/LocationCreate";
import LocationEdit from "components/LocationEdit";
import LocationList from "components/LocationList";
import LocationShow from "components/LocationShow";

import ServiceCreate from "components/ServiceCreate";
import ServiceEdit from "components/ServiceEdit";
import ServiceList from "components/ServiceList";
import ServiceShow from "components/ServiceShow";

import UserList from "components/UserList";
import UserEdit from "components/UserEdit";
import UserShow from "components/UserShow";

import ArtistList from "../components/ArtistList";
import ArtistCreate from "../components/ArtistCreate";
import ArtistEdit from "../components/ArtistEdit";
import ArtistShow from "../components/ArtistShow";

import MenuList from "components/MenuList";
import MenuCreate from "components/MenuCreate";
import MenuEdit from "components/MenuEdit";
import MenuShow from "components/MenuShow";

import * as Cognito from "providers/cognito";
import * as User from "providers/user";
import { createApolloClient } from "utils/create-apollo-client";

import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
// @ts-ignore
import vietnameseMessages from "ra-language-vietnamese";
import {
  customEnglishMessages,
  customVietnameseMessages
} from "utils/messageTranslations";

import AvailabilityList from "components/AvailabilityList";

import AppointmentList from "components/AppointmentList";
import AppointmentEdit from "components/AppointmentEdit";
import AppointmentShow from "components/AppointmentShow";

// import FacilityList from "components/FacilityList";
// import FacilityCreate from "components/FacilityCreate";
// import FacilityEdit from "components/FacilityEdit";
// import FacilityShow from "components/FacilityShow";

import PlatformShow from "components/PlatformShow";
import PlatformEdit from "components/PlatformEdit";

import theme from "themes/theme";
import OrderList from "components/OrderList";
import OrderShow from "components/OrderShow";
import { AnimatedLoader } from "components/AnimatedLoader";
import ServicesCalendarContainer from "components/ServicesCalendarContainer";
import { ApolloProvider } from "@apollo/client";
import UserCreateContainer from "components/UserCreate";
import AppointmentServiceCalendarList from "components/AppointmentServiceCalendarList";
import TippingList from "components/TippingList";
import CancelledAppointmentsReportingList from "components/CancelledAppointmentsReportingList";
import TransactionsReportList from "components/TransactionsReportList";
import DashboardHome from "components/DashboardHome";
import DashboardRoleArtistHome from "components/artistDashboard/DashboardRoleArtistHome";

const translations = {
  en: { ...englishMessages, ...customEnglishMessages },
  vi: { ...vietnameseMessages, ...customVietnameseMessages }
};

const i18nProvider = polyglotI18nProvider(
  (locale: string) => {
    return translations[locale as keyof typeof translations];
  },
  "en", // default locale
  [
    { locale: "en", name: "English" },
    { locale: "vi", name: "Vietnamese" }
  ]
);

export default function Dashboard() {
  const { user } = Cognito.useCognito();

  if (user?.access_token) return <HydratedDashboard />;

  return null;
}

const HydratedDashboard = () => {
  const { user: cognitoUser } = Cognito.useCognito();
  const { user, permissions } = User.useUser();
  const client = createApolloClient(cognitoUser?.access_token || "");
  const url = process.env.REACT_APP_API_CORE_URL || "";
  const dataProvider = useDataProvider(
    { client },
    url,
    cognitoUser?.access_token || ""
  );
  const translate = useTranslate();

  if (!permissions.companyId) return <AnimatedLoader />;

  if (!dataProvider)
    return (
      <>
        <Helmet>
          <title>Pointment | Dashboard</title>
          <meta property="og:title" content="Pointment | Authenticate" />
        </Helmet>
        <AnimatedLoader />
      </>
    );

  return (
    <>
      <ApolloProvider client={client}>
        <Helmet>
          <title>Pointment | Dashboard</title>
          <meta property="og:title" content="Pointment | Authenticate" />
        </Helmet>
        <Admin
          i18nProvider={i18nProvider}
          dashboard={
            permissions?.isCompanyLimited || permissions?.isCompanyBasic
              ? CompanyShow
              : permissions?.isArtist
              ? DashboardRoleArtistHome
              : DashboardHome
          }
          basename="/dashboard"
          dataProvider={dataProvider}
          layout={Layout}
          theme={theme}
        >
          <Resource
            name="Location"
            list={LocationList}
            create={permissions.isArtist ? undefined : LocationCreate}
            show={LocationShow}
            edit={permissions.isArtist ? undefined : LocationEdit}
            options={{ label: translate("pagenames.locations") }}
          />
          <Resource
            name="Menu"
            list={MenuList}
            create={permissions.isArtist ? undefined : MenuCreate}
            edit={permissions.isArtist ? undefined : MenuEdit}
            show={MenuShow}
            recordRepresentation={(record) => record.name}
            options={{ label: translate("pagenames.menus") }}
          />
          <Resource
            name="Service"
            list={ServiceList}
            create={permissions.isArtist ? undefined : ServiceCreate}
            edit={permissions.isArtist ? undefined : ServiceEdit}
            show={ServiceShow}
            recordRepresentation={(record) => record.name}
            options={{ label: translate("pagenames.services") }}
          />
          <Resource
            name="AddOns"
            list={ServiceList}
            create={permissions.isArtist ? undefined : ServiceCreate}
            edit={permissions.isArtist ? undefined : ServiceEdit}
            show={ServiceShow}
            recordRepresentation={(record) => record.name}
            options={{ label: translate("pagenames.addons") }}
          />
          {/* <Resource
          name="Appointment"
          list={AppointmentList}
          // create={permissions.isArtist ? undefined : AppointmentCreate}
          edit={permissions.isArtist ? undefined : AppointmentEdit}
          show={AppointmentShow}
          options={{ label: translate("pagenames.appointment") }}
          recordRepresentation={(record) => record.name}
        >
          <Route
            path="/:dashboard/:Appointment/Calendar"
            element={<CustomAppointmentList />}
          />
        </Resource> */}
          <Resource
            name="Appointment"
            list={AppointmentList}
            // create={permissions.isArtist ? undefined : AppointmentCreate}
            edit={permissions.isArtist ? undefined : AppointmentEdit}
            show={AppointmentShow}
            options={{ label: translate("pagenames.appointment") }}
            recordRepresentation={(record) => record.name}
          />
          <Resource
            name="Services-calendar"
            list={ServicesCalendarContainer}
            options={{ label: "Services calendar" }}
            recordRepresentation={(record) => record.name}
          />
          {/* <Resource
            name="Facility"
            list={FacilityList}
            create={permissions.isArtist ? undefined : FacilityCreate}
            edit={permissions.isArtist ? undefined : FacilityEdit}
            show={FacilityShow}
            recordRepresentation={(record) => record.name}
            options={{ label: translate("pagenames.equipment") }}
          /> */}
          <Resource
            name="Availability"
            list={AvailabilityList}
            options={{ label: translate("pagenames.availability") }}
          />
          {!permissions.isArtist && (
            <>
              <Resource
                name="Customer"
                list={UserList}
                create={permissions.isArtist ? undefined : UserCreateContainer}
                show={UserShow}
                edit={UserEdit}
                recordRepresentation={(record) =>
                  `${record.first_name} ${record.last_name}`
                }
                options={{ label: translate("pagenames.users") }}
              />
              <Resource
                name="Artist"
                list={ArtistList}
                create={ArtistCreate}
                show={ArtistShow}
                edit={ArtistEdit}
                options={{ label: translate("pagenames.artists") }}
              />
              <Resource
                name="Company"
                list={
                  user.impersonate_company_id || !permissions.isSuperAdmin
                    ? undefined
                    : CompanyList
                }
                show={CompanyShow}
                edit={CompanyEdit}
                recordRepresentation={(record) => record.name}
                options={{ label: translate("pagenames.companies") }}
              />
              {permissions.isSuperAdmin && !user.impersonate_company_id && (
                <>
                  <Resource
                    name="Platform"
                    show={PlatformShow}
                    edit={PlatformEdit}
                    options={{ label: translate("pagenames.platform") }}
                  />
                  <Resource
                    name="User"
                    list={UserList}
                    create={
                      permissions.isArtist ? undefined : UserCreateContainer
                    }
                    show={UserShow}
                    edit={UserEdit}
                    recordRepresentation={(record) =>
                      `${record.first_name} ${record.last_name}`
                    }
                    options={{ label: translate("pagenames.users") }}
                  />
                </>
              )}
              {(permissions.isSuperAdmin ||
                permissions.role.role === "OWNER" ||
                user.impersonate_company_id) && (
                <>
                  <Resource
                    name="Appointment-service-reporting"
                    list={AppointmentServiceCalendarList}
                    recordRepresentation={(record) => record.name}
                    options={{
                      label: translate("pagenames.appointment_services")
                    }}
                  />
                  <Resource
                    name="Tipping-reporting"
                    list={TippingList}
                    recordRepresentation={(record) => record.name}
                    options={{
                      label: translate("pagenames.tipping")
                    }}
                  />
                  <Resource
                    name="Transactions-reporting"
                    list={TransactionsReportList}
                    recordRepresentation={(record) => record.name}
                    options={{
                      label: translate("pagenames.transactions")
                    }}
                  />
                  <Resource
                    name="Cancelled-appointment-reporting"
                    list={CancelledAppointmentsReportingList}
                    recordRepresentation={(record) => record.name}
                    options={{
                      label: translate("pagenames.cancelled_appointments")
                    }}
                  />
                </>
              )}
              <Resource
                key="Appointment-order"
                name="Appointment-order"
                list={OrderList}
                show={OrderShow}
                options={{ label: translate("pagenames.appointment_order") }}
              />
              <Resource
                key="Walk-in-order"
                name="Walk-in-order"
                list={OrderList}
                show={OrderShow}
                options={{ label: translate("pagenames.walk_in_order") }}
              />
            </>
          )}
        </Admin>
      </ApolloProvider>
    </>
  );
};
